import React, { useEffect, useRef, useState } from 'react';

import { useIsFocused, useNavigation } from '@react-navigation/native';
import { usePathname, useRouter } from 'expo-router';
import { StatusBar } from 'expo-status-bar';
import {
  PanResponder,
  StyleSheet,
  TouchableOpacity,
  View,
  useWindowDimensions,
} from 'react-native';
import Animated, { useAnimatedStyle, useSharedValue, withTiming } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { useModalFocusTrap } from 'hooks/accessibility/use-modal-focus-trap';
import { AccountDrawerPage } from 'pages/account';
import { t } from 'state/intl/translations';
import { primitive, spacing } from 'styles/constants';
import { TRANSITION_ANIMATION_TIME_MS } from 'utils/constants';
import { isWeb } from 'utils/environment';
import { routes } from 'utils/routing';

const DRAWER_WIDTH = 0.9;

export default function AccountDrawerRoute() {
  const navigation = useNavigation();
  const isFocused = useIsFocused();
  const { top, bottom } = useSafeAreaInsets();
  const pathname = usePathname();
  const router = useRouter();
  const { width } = useWindowDimensions();
  // Defers mounting the drawer contents until the native transparent route has mounted.
  const [show, setShow] = useState(false);
  const shouldHideForRoute = !pathname?.startsWith(routes.account);
  const isClosingRef = useRef(false);

  const translateX = useSharedValue(-width * DRAWER_WIDTH);
  const overlayOpacity = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [{ translateX: translateX.value }],
  }));

  const overlayAnimatedStyle = useAnimatedStyle(() => {
    return { opacity: overlayOpacity.value };
  });

  const initialFocusRef = useRef(true);
  useEffect(() => {
    const removeBlurListener = navigation.addListener('blur', () => {
      translateX.value = withTiming(-width * DRAWER_WIDTH, {
        duration: TRANSITION_ANIMATION_TIME_MS,
      });
      overlayOpacity.value = withTiming(0, {
        duration: TRANSITION_ANIMATION_TIME_MS,
      });
    });

    const removeFocusListener = navigation.addListener('focus', () => {
      if (initialFocusRef.current) {
        initialFocusRef.current = false;
        return;
      }
      translateX.value = withTiming(0, {
        duration: TRANSITION_ANIMATION_TIME_MS,
      });
      overlayOpacity.value = withTiming(0.5, {
        duration: TRANSITION_ANIMATION_TIME_MS,
      });
    });

    return () => {
      removeBlurListener();
      removeFocusListener();
    };
  }, [navigation, overlayOpacity, translateX, width]);

  React.useEffect(() => {
    translateX.value = withTiming(0, { duration: TRANSITION_ANIMATION_TIME_MS });
    overlayOpacity.value = withTiming(0.5, { duration: TRANSITION_ANIMATION_TIME_MS });
  }, [overlayOpacity, translateX]);

  useEffect(() => {
    if (!shouldHideForRoute) {
      return;
    }

    setShow(false);
    translateX.value = -width * DRAWER_WIDTH;
    overlayOpacity.value = 0;
  }, [overlayOpacity, shouldHideForRoute, translateX, width]);

  useEffect(() => {
    if (shouldHideForRoute) {
      return;
    }

    // Expo Router can reuse this drawer route after a nested/static page closes. The off-route
    // effect leaves the shared values in their hidden state, so restore both animations here;
    // otherwise an invisible full-screen drawer remains mounted and blocks page interaction.
    const timeout = setTimeout(() => {
      setShow(true);
      translateX.value = withTiming(0, { duration: TRANSITION_ANIMATION_TIME_MS });
      overlayOpacity.value = withTiming(0.5, { duration: TRANSITION_ANIMATION_TIME_MS });
    }, 0);

    return () => clearTimeout(timeout);
  }, [overlayOpacity, shouldHideForRoute, translateX]);

  const shouldTrapDrawerFocus = isFocused && show && !shouldHideForRoute;

  const {
    modalRef,
    restoreOutsideElements,
    restoreFocusToTrigger,
    getModalA11yProps,
    getBackdropA11yProps,
  } = useModalFocusTrap({
    active: shouldTrapDrawerFocus,
    label: t('myAccount'),
    initialFocusTarget: 'none',
    isClosingRef,
  });

  const handleClose = () => {
    if (isClosingRef.current) {
      return;
    }

    isClosingRef.current = true;
    translateX.value = withTiming(-width * DRAWER_WIDTH, {
      duration: TRANSITION_ANIMATION_TIME_MS,
    });
    overlayOpacity.value = withTiming(0, { duration: TRANSITION_ANIMATION_TIME_MS });
    setTimeout(() => {
      restoreOutsideElements();
      router.dismiss();
      restoreFocusToTrigger();
    }, TRANSITION_ANIMATION_TIME_MS);
  };

  if (shouldHideForRoute) {
    return <View pointerEvents="none" style={styles.hiddenOffRoute} />;
  }

  const panResponder = PanResponder.create({
    onMoveShouldSetPanResponder: (evt, gestureState) =>
      Math.abs(gestureState.dx) > 10 && Math.abs(gestureState.dx) > Math.abs(gestureState.dy),
    onPanResponderMove: (evt, gestureState) => {
      if (gestureState.dx < 0) {
        translateX.value = gestureState.dx;
      }
    },
    onPanResponderRelease: (evt, gestureState) => {
      if (gestureState.dx < (-DRAWER_WIDTH * width) / 3) {
        handleClose();
      } else {
        translateX.value = withTiming(0, { duration: TRANSITION_ANIMATION_TIME_MS });
      }
    },
  });

  return (
    <View style={styles.fullScreen}>
      <StatusBar translucent />
      <Animated.View style={[styles.grayBackground, overlayAnimatedStyle]} />
      <Animated.View {...panResponder.panHandlers} style={styles.panResponderWrapper}>
        <Animated.View
          ref={modalRef}
          {...getModalA11yProps({ nativeRole: 'menu' })}
          style={[
            styles.drawer,
            { paddingTop: top || spacing.$4, paddingBottom: bottom || spacing.$4 },
            animatedStyle,
          ]}
        >
          {show && <AccountDrawerPage handleClose={handleClose} />}
        </Animated.View>
        <TouchableOpacity
          testID="account-drawer-backdrop"
          style={styles.seeThroughBackground}
          onPress={handleClose}
          {...getBackdropA11yProps()}
        />
      </Animated.View>
    </View>
  );
}

const styles = StyleSheet.create({
  fullScreen: {
    flex: 1,
    flexDirection: 'row',
  },
  panResponderWrapper: {
    flex: 1,
    flexDirection: 'row',
  },
  grayBackground: {
    backgroundColor: 'rgba(0, 0, 0, 1)',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  drawer: {
    width: '90%',
    backgroundColor: primitive.$mayoEggWhite,
    padding: 16,
    ...(isWeb ? { maxWidth: 912 } : null),
  },
  seeThroughBackground: {
    flex: 1,
  },
  hiddenOffRoute: {
    flex: 1,
    backgroundColor: 'transparent',
  },
});
